import React, { useEffect, useState, useRef, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, UserIcon, TagIcon } from 'lucide-react';
import { portfolioItems } from '../data/portfolioData';
import ImageGallery from '../components/ImageGallery';
const ProjectDetail = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  useEffect(() => {
    const foundProject = portfolioItems.find(item => item.title.toLowerCase().replace(/\s+/g, '-') === slug);
    if (foundProject) {
      setProject(foundProject);
    } else {
      navigate('/');
    }
  }, [slug, navigate]);
  if (!project) {
    return <div className="flex items-center justify-center h-64" data-id="element-323">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" data-id="element-324"></div>
      </div>;
  }
  const isPhotoProject = project?.category === 'Photo' || Array.isArray(project?.category) && project?.category.includes('Photo');
  const galleryImages = project?.galleryImages || [];
  const AnimatedGif = ({
    url
  }: {
    url: string;
  }) => {
    return <motion.div className="w-full max-w-[1200px] mx-auto my-8 md:my-16" whileHover={{
      scale: 1.02
    }} transition={{
      duration: 0.3
    }} data-id="element-325">
        <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{
        paddingTop: '56.25%'
      }} data-id="element-326">
          <img src={url} alt="Animation" className="absolute top-0 left-0 w-full h-full object-cover" style={{
          border: 'none'
        }} data-id="element-327" />
        </div>
      </motion.div>;
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.5
  }} className="bg-white/80 backdrop-blur-md rounded-3xl overflow-hidden shadow-lg" data-id="element-328">
      <div className={`relative w-full ${isPhotoProject ? 'h-[45vh] md:h-[65vh]' : 'h-[30vh] md:h-[45vh]'}`} data-id="element-329">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-330" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" data-id="element-331"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 lg:p-10" data-id="element-332">
          <div className="max-w-6xl mx-auto w-full" data-id="element-333">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-334">
              {project.title}
            </motion.h1>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-wrap gap-3 mb-4" data-id="element-335">
              <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium
                ${project.category === 'Video' ? 'bg-indigo-500/90 text-white' : project.category === 'Photo' ? 'bg-rose-500/90 text-white' : 'bg-teal-500/90 text-white'}`} data-id="element-336">
                <TagIcon className="w-4 h-4 mr-1" data-id="element-337" />
                {project.category}
              </span>
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-white/20 text-white" data-id="element-338">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-339" />
                {project.year}
              </span>
              <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-white/20 text-white" data-id="element-340">
                <UserIcon className="w-4 h-4 mr-1" data-id="element-341" />
                {project.client}
              </span>
            </motion.div>
            {project.shortDescription && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="max-w-3xl" data-id="element-342">
                <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-343">
                  {project.shortDescription}
                </p>
              </motion.div>}
          </div>
        </div>
        <motion.button onClick={() => navigate(-1)} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="absolute top-6 left-6 bg-white/20 backdrop-blur-md hover:bg-white/40 p-2 rounded-full text-white transition-colors duration-300" data-id="element-344">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-345" />
        </motion.button>
      </div>
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className={`space-y-12 ${isPhotoProject ? 'py-12 md:py-16' : 'py-8 md:py-12'}`} data-id="element-347">
          {project.animationUrl && <AnimatedGif url={project.animationUrl} data-id="element-348" />}
          {project.videoUrl && <div className="w-full max-w-[1200px] mx-auto" data-id="element-349">
              <div className="relative w-full" style={{
            paddingTop: '56.25%'
          }} data-id="element-350">
                <iframe src={project.videoUrl} title={project.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" className="absolute top-0 left-0 w-full h-full rounded-xl shadow-lg" style={{
              border: 'none'
            }} data-id="element-351" />
              </div>
            </div>}
          {isPhotoProject && galleryImages.length > 0 && <div className="max-w-7xl mx-auto" data-id="element-352">
              <ImageGallery images={galleryImages} data-id="element-353" />
            </div>}
          {project.description && <div className="max-w-4xl mx-auto" data-id="element-354">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-355">
                Über das Projekt
              </h2>
              <div className="prose prose-lg max-w-none" data-id="element-356">
                <div className="whitespace-pre-wrap text-gray-700" data-id="element-357">
                  {project.description}
                </div>
              </div>
            </div>}
        </motion.div>
      </div>
    </motion.div>;
};
export default ProjectDetail;